import { campaignDetailsData_mock, impactStatsData_mock, marketStatsData_mock } from 'mockData/campaignDetailsData';
import { CampaignDetails, CampaignMetadata } from 'modules/Types';
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import useBrand, { BrandExistsState } from './useBrand';
import {
    getPresaveCampaignPresaveStatus_api,
    retryApplyPresaves_api,
    getCampaignAnalyticsV2_api,
    getCampaignAnalytics_impactStats_v2_api,
    CampaignImpactStatsMetric,
    CampaignDetailsData,
    updateCampaignPauseStatus_api, duplicateCampaign_api, getCampaign_api, downloadCampaignReport_api,
    PresaveCampaignPresaveStatus,
    getPresaveCampaignAnalytics_api,
    adminRetryCampaign_api,
    renameCampaign_api,
    updateCampaignBudget_api,
    updateCampaignEndDate_api,
    pauseResumeAsset_api,
    uploadNewAssets_api,
    archiveCampaign_api,
    unarchiveCampaign_api,
    processAgentCampaign_api,
    extendCampaign_api
} from 'api/campaignApi';
import { CreativeSelectorPost } from './useCampaignBuilder';

interface CampaignDetailsState {
    // State
    campaign: CampaignDetails | null;
    campaignId: number | null;
    associatedContentSlug: string | null;
    campaignAnalyticsData: CampaignDetailsData | null;
    marketStatsData: any | null;
    impactStatsData: CampaignImpactStatsMetric[] | null;
    isLoadingCampaign: boolean;
    isLoadingCampaignAnalytics: boolean;
    isLoadingMarketStatsData: boolean;
    isLoadingImpactData: boolean;
    isRetryingApplyPresaves: boolean;

    showEditBudgetModal: boolean;
    showEditEndDateModal: boolean;

    isPausingResumingCampaign: boolean;
    isDuplicatingCampaign: boolean;
    isRenamingCampaign: boolean;
    isEditingCampaignEndDate: boolean;
    isEditingCampaignBudget: boolean;
    isArchivingCampaign: boolean;
    isRetryingCampaign: boolean;
    isDownloadingReport: boolean;
    isPausingResumingAsset: boolean;
    isUploadingNewAssets: boolean;
    isProcessingAgentCampaign: boolean;
    isExtendingCampaign: boolean;


    // Setters
    setCampaignId: (campaignId: number) => void;
    setCampaignDetails: (details: any) => void;
    setMarketStatsData: (data: any) => void;
    setIsLoadingCampaignDetails: (loading: boolean) => void;
    setIsLoadingMarketStatsData: (loading: boolean) => void;
    setIsLoadingImpactData: (loading: boolean) => void;
    setShowEditBudgetModal: (show: boolean) => void;
    setShowEditEndDateModal: (show: boolean) => void;

    // Methods
    getPresaveCampaignPresaveStatus: (campaignId: number) => Promise<PresaveCampaignPresaveStatus>;
    retryApplyPresaves: (campaignId: number) => Promise<any>;
    getPresaveCampaignAnalytics: (campaignId: number) => Promise<any>;
    getCampaign: (campaignId: number) => Promise<CampaignDetails | null | undefined>
    getCampaignAnalytics: (campaignId: number) => Promise<any>;
    getMarketStatsData: (campaignId: number) => Promise<any>;
    getImpactData: (campaignId: number) => Promise<any>;

    updateCampaignMetadata: (data: CampaignMetadata) => void;

    // actions on campaign
    pauseCampaign: (campaignId: number) => Promise<CampaignDetails>;
    resumeCampaign: (campaignId: number, allAssets?: boolean) => Promise<CampaignDetails>;
    duplicateCampaign: (campaignId: number) => Promise<any>;
    renameCampaign: (campaignId: number, newName: string) => Promise<string | undefined>;
    editBudget: (campaignId: number, newBudget: number) => Promise<any>;
    editEndDate: (campaignId: number, newDate: string) => Promise<any>;
    archiveCampaign: (campaignId: number) => Promise<any>;
    unarchiveCampaign: (campaignId: number) => Promise<any>;
    resetCampaignDetails: () => void;
    adminRetryCampaign: (campaignId: number, socialPlatform?: 'facebook' | 'tiktok') => Promise<any>;
    downloadCampaignReport: (campaignId: number) => Promise<any>;
    pauseResumeAsset: (campaignId: number, creativeId: string, pause: boolean) => Promise<CampaignDetails>;
    uploadNewAssets: (campaignId: number, assets: CreativeSelectorPost[]) => Promise<CampaignDetails>;
    processAgentCampaign: (campaignId: number, brandId?: number) => Promise<any>;
    extendCampaign: (campaignId: number, newEndDate: string, newBudget: number) => Promise<any>;

}

export interface CampaignExistsState extends CampaignDetailsState {
    campaignId: number;
    campaign: CampaignDetails | any | null;
    uploadNewAssets: (campaignId: number, assets: CreativeSelectorPost[]) => Promise<CampaignDetails>;
}

const useCampaignDetails = create(immer<CampaignDetailsState>((set, get) => ({
    // State
    isPausingResumingAsset: false,
    campaignId: null,
    campaign: null,
    associatedContentSlug: null,
    campaignAnalyticsData: null,
    marketStatsData: null,
    impactStatsData: null,
    isLoadingCampaign: true,
    isLoadingCampaignAnalytics: true,
    isLoadingMarketStatsData: true,
    isLoadingImpactData: true,
    isPausingResumingCampaign: false,
    isDuplicatingCampaign: false,
    isRenamingCampaign: false,
    isEditingCampaignEndDate: false,
    isEditingCampaignBudget: false,
    isDownloadingReport: false,
    isRetryingApplyPresaves: false,
    isArchivingCampaign: false,
    isRetryingCampaign: false,
    isUploadingNewAssets: false,
    isProcessingAgentCampaign: false,
    isExtendingCampaign: false,

    showEditBudgetModal: false,
    showEditEndDateModal: false,

    // Setters
    updateCampaignMetadata: (data: CampaignMetadata) => {
        set((state) => {
            if (state.campaign) {
                state.campaign.campaign_metadata = {
                    ...state.campaign.campaign_metadata,
                    ...data
                }
            }
        })
    },
    setCampaignId: (campaignId: number) => { set((state) => { state.campaignId = campaignId }) },
    setCampaignDetails: (details) => { set((state) => { state.campaignAnalyticsData = details }) },
    setMarketStatsData: (data) => { set((state) => { state.marketStatsData = data }) },
    setIsLoadingCampaignDetails: (loading) => { set((state) => { state.isLoadingCampaignAnalytics = loading }) },
    setIsLoadingMarketStatsData: (loading) => { set((state) => { state.isLoadingMarketStatsData = loading }) },
    setIsLoadingImpactData: (loading) => { set((state) => { state.isLoadingImpactData = loading }) },
    setShowEditBudgetModal: (show: boolean) => { set((state) => { state.showEditBudgetModal = show }) },
    setShowEditEndDateModal: (show: boolean) => { set((state) => { state.showEditEndDateModal = show }) },

    getPresaveCampaignPresaveStatus: async (campaignId: number): Promise<PresaveCampaignPresaveStatus> => {
        const response = await getPresaveCampaignPresaveStatus_api({ campaignId })
        return response
    },

    retryApplyPresaves: async (campaignId: number): Promise<any> => {
        try {
            set((state) => { state.isRetryingApplyPresaves = true })
            const response = await retryApplyPresaves_api({ campaignId })
            return response
        } catch (error) {
            console.error('Error retrying apply presaves:', error);
            throw error;
        } finally {
            set((state) => { state.isRetryingApplyPresaves = false })
        }
    },

    getPresaveCampaignAnalytics: async (campaignId: number) => {
        const response = await getPresaveCampaignAnalytics_api({ campaignId })
        return response
    },

    getCampaign: async (campaignId: number): Promise<CampaignDetails | null | undefined> => {
        set((state) => {
            state.campaignId = campaignId
            state.isLoadingCampaign = true
        })

        try {
            let campaignData = await getCampaign_api(campaignId)
            if (!campaignData) {
                throw new Error('useCampaignDetails::getCampaignDetails:: No campaign data found')
            }

          
            set((state) => {
                state.campaign = campaignData
            })

            return campaignData
        } catch (err) {
            set((state) => {
                // state.error = err instanceof Error ? err.message : 'An error occurred'
            })
        } finally {
            set((state) => {
                state.isLoadingCampaign = false
            })
        }
    },

    // getEditingPermissions: (): CampaignEditingPermissions => {
    //     const { campaign } = get();
        
    //     if (!campaign) {
    //         // Return all false permissions when no campaign is loaded
    //         return {
    //             canEdit: false,
    //             canRename: false,
    //             canDuplicate: false,
    //             canEditBudget: false,
    //             canEditEndDate: false,
    //             canUploadAssets: false,
    //             canEditAssets: false,
    //             canPauseResumeAssets: false,
    //             canPauseResume: false,
    //             canArchive: false,
    //             canUnarchive: false,
    //             isPaused: false,
    //             isArchived: false,
    //             isFinished: false,
    //             restrictions: {
    //                 isTikTokCampaign: false,
    //                 isExpressSetup: false,
    //                 isAdBoxCampaign: false,
    //                 hasInvalidStatus: false,
    //                 invalidStatuses: []
    //             }
    //         };
    //     }

    //     const { campaign_metadata, status: campaignStatus } = campaign;
    //     const { v2_campaign_input, playbook } = campaign_metadata || {};
    //     const isAdBoxCampaign = Boolean((campaign_metadata as any)?.adbox_checkout);
    //     const { usesExpressSetup } = (v2_campaign_input as any) || {};

    //     // Determine ad platform
    //     const adPlatform = PLAYBOOK_TYPE_INFO[playbook as PlaybookType]?.adPlatform;
        
    //     // Basic campaign properties
    //     const isTikTokCampaign = adPlatform === AdPlatform.TIKTOK;
    //     const isExpressSetup = Boolean(usesExpressSetup);
    //     const isPaused = campaignStatus === CampaignStatus.PAUSED;
    //     const isArchived = campaignStatus === CampaignStatus.ARCHIVED;
    //     const isFinished = campaignStatus === CampaignStatus.FINISHED;

    //     // Define status groups
    //     const nonEditableStatuses = [
    //         CampaignStatus.DENIED,
    //         CampaignStatus.ERROR,
    //         CampaignStatus.FINISHED,
    //         CampaignStatus.ARCHIVED,
    //         CampaignStatus.CANCELLED
    //     ];

    //     const nonPausableStatuses = [
    //         CampaignStatus.DENIED,
    //         CampaignStatus.ERROR,
    //         CampaignStatus.FINISHED,
    //         CampaignStatus.ARCHIVED,
    //         CampaignStatus.CANCELLED
    //     ];

    //     const nonAssetUploadStatuses = [
    //         CampaignStatus.DENIED,
    //         CampaignStatus.DRAFT,
    //         CampaignStatus.ERROR,
    //         CampaignStatus.TO_RETRY,
    //         CampaignStatus.FINISHED,
    //         CampaignStatus.ARCHIVED,
    //         CampaignStatus.CANCELLED
    //     ];

    //     const archivableStatuses = [
    //         CampaignStatus.ACTIVE,
    //         CampaignStatus.IN_REVIEW,
    //         CampaignStatus.TO_RETRY,
    //         CampaignStatus.PAUSED,
    //         CampaignStatus.WITH_ISSUES
    //     ];

    //     // Status-based restrictions
    //     const hasInvalidEditStatus = nonEditableStatuses.includes(campaignStatus as CampaignStatus);
    //     const hasInvalidPauseStatus = nonPausableStatuses.includes(campaignStatus as CampaignStatus);
    //     const hasInvalidAssetStatus = nonAssetUploadStatuses.includes(campaignStatus as CampaignStatus);
    //     const canArchiveBasedOnStatus = archivableStatuses.includes(campaignStatus as CampaignStatus);

    //     // Core editing permission - used as base for most other permissions
    //     const canEdit = !isTikTokCampaign && !hasInvalidEditStatus && !isAdBoxCampaign && !isExpressSetup;

    //     return {
    //         // General editing permissions
    //         canEdit,
    //         canRename: !isTikTokCampaign, // Only platform restriction
    //         canDuplicate: true, // Always allowed
            
    //         // Budget and schedule permissions  
    //         canEditBudget: canEdit,
    //         canEditEndDate: canEdit,
            
    //         // Asset permissions
    //         canUploadAssets: !hasInvalidAssetStatus,
    //         canEditAssets: adPlatform === AdPlatform.META && !isExpressSetup && campaignStatus !== CampaignStatus.CANCELLED && campaignStatus !== CampaignStatus.ARCHIVED,
    //         canPauseResumeAssets: adPlatform === AdPlatform.META && !isExpressSetup && campaignStatus !== CampaignStatus.CANCELLED && campaignStatus !== CampaignStatus.ARCHIVED,
            
    //         // Campaign state permissions
    //         canPauseResume: !isTikTokCampaign && !isExpressSetup && !hasInvalidPauseStatus,
    //         canArchive: !isTikTokCampaign && !isExpressSetup && canArchiveBasedOnStatus,
    //         canUnarchive: isArchived,
            
    //         // Status-based information
    //         isPaused,
    //         isArchived,
    //         isFinished,
            
    //         // Reasons for restrictions (for better UX messaging)
    //         restrictions: {
    //             isTikTokCampaign,
    //             isExpressSetup,
    //             isAdBoxCampaign,
    //             hasInvalidStatus: hasInvalidEditStatus,
    //             invalidStatuses: nonEditableStatuses.filter(status => status === campaignStatus)
    //         }
    //     };
    // },

    // Methods
    getCampaignAnalytics: async (campaignId: number) => {
        try {
            let MOCK_DATA = false;

            set((state) => { state.isLoadingCampaignAnalytics = true })
            if (MOCK_DATA) {
                const campaignDetailsDataLocal = campaignDetailsData_mock;
                set((state) => {
                    state.campaignAnalyticsData = campaignDetailsDataLocal as unknown as CampaignDetailsData
                    state.associatedContentSlug = 'test-song'
                })
                return campaignDetailsDataLocal;
            } else {
                const campaignDetailsDataLocal = await getCampaignAnalyticsV2_api({ campaignId });
                set((state) => {
                    state.campaignAnalyticsData = campaignDetailsDataLocal || null
                    state.associatedContentSlug = 'test-song'
                })
                return campaignDetailsDataLocal;
            }
        } catch (error) {
            console.error('Error fetching campaign details:', error);
        } finally {
            set((state) => {
                state.isLoadingCampaignAnalytics = false
            })
        }
    },
    getMarketStatsData: async (campaignId: number) => {
        try {
            set((state) => { state.isLoadingMarketStatsData = true })
            const marketStatsDataLocal = marketStatsData_mock;
            set((state) => {
                state.marketStatsData = marketStatsDataLocal
            })
            return marketStatsDataLocal;
        } catch (error) {
            console.error('Error fetching market stats:', error);
        } finally {
            set((state) => { state.isLoadingMarketStatsData = false })
        }
    },

    ////// MEGH STUB
    getImpactData: async (campaignId: number) => {
        const { currentBrand } = useBrand.getState() as BrandExistsState
        const {
            associatedContentSlug: contentSlug
        } = get()

        let MOCK_DATA = false;

        try {
            set((state) => { state.isLoadingImpactData = true })
            if (MOCK_DATA) {
                const impactStatsDataLocal = impactStatsData_mock;
                set((state) => {
                    state.impactStatsData = impactStatsDataLocal
                })
                return impactStatsDataLocal;
            } else {
                const impactStatsDataLocal = await getCampaignAnalytics_impactStats_v2_api({ campaignId });
                set((state) => {
                    state.impactStatsData = impactStatsDataLocal || []
                })
                return impactStatsDataLocal || [];
            }
        } catch (error) {
            console.error('Error fetching campaign details:', error);
        } finally {
            set((state) => {
                state.isLoadingImpactData = false
            })
        }

        return;
    },

    pauseCampaign: async (campaignId: number): Promise<CampaignDetails> => {
        try {
            set((state) => { state.isPausingResumingCampaign = true })
            const response = await updateCampaignPauseStatus_api({
                campaignId,
                pause: true
            });
            set((state) => {
                state.campaign = response
            })
            return response;
        } catch (error) {
            console.error('Error pausing campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isPausingResumingCampaign = false })
        }
    },

    // if allAssets is true, resume all assets for the campaign (the campaign, adsets, and ads)
    resumeCampaign: async (campaignId: number, allAssets?: boolean): Promise<CampaignDetails> => {
        try {
            set((state) => { state.isPausingResumingCampaign = true })
            const response = await updateCampaignPauseStatus_api({
                campaignId,
                pause: false,
                allAssets
            });
            set((state) => {
                state.campaign = response
            })
            return response;
        } catch (error) {
            console.error('Error pausing campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isPausingResumingCampaign = false })
        }
    },

    duplicateCampaign: async (campaignId) => {
        try {
            set((state) => { state.isDuplicatingCampaign = true })
            const res = await duplicateCampaign_api({ campaignId });
            return res;
        } catch (error) {
            console.error('Error duplicating campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isDuplicatingCampaign = false })
        }
    },

    renameCampaign: async (campaignId, newName) => {
        try {
            set((state) => { state.isRenamingCampaign = true });
            const res = await renameCampaign_api({ campaignId, newName });
            // Update name fields locally without replacing entire campaign object
            set((state) => {
                if (state.campaign) {
                    // Update the display name in campaign metadata
                    if (state.campaign.campaign_metadata) {
                        state.campaign.campaign_metadata.friendlyName = newName;
                    }
                    // Update campaign_name if it exists
                    if (state.campaign.campaign_name !== undefined) {
                        state.campaign.campaign_name = newName;
                    }
                }
            });
            
            return res.data;
        } catch (error) {
            console.error('Error renaming campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isRenamingCampaign = false });
        }
    },

    editBudget: async (campaignId, newBudget) => {
        try {
            set((state) => { state.isEditingCampaignBudget = true });
            const res = await updateCampaignBudget_api({ campaignId, budget: newBudget });
            
            // Refresh campaign data after editing budget
            await get().getCampaign(campaignId);
            
            return res.data;
        } catch (error) {
            console.error('Error editing budget: ', error);
            throw error;
        } finally {
            set((state) => { state.isEditingCampaignBudget = false });
        }
    },

    editEndDate: async (campaignId, newDate) => {
        try {
            set((state) => { state.isEditingCampaignEndDate = true });
            const res = await updateCampaignEndDate_api({ campaignId, endDate: newDate });
            
            // Refresh campaign data after editing end date
            await get().getCampaign(campaignId);
            
            return res.data;
        } catch (error) {
            console.error('Error editing endDate: ', error);
            throw error;
        } finally {
            set((state) => { state.isEditingCampaignEndDate = false });
        }
    },

    archiveCampaign: async (campaignId) => {
        try {
            set((state) => { state.isArchivingCampaign = true })
            const response = await archiveCampaign_api({ campaignId });
            set((state) => {
                state.campaign = response
            })
            return response;
        } catch (error) {
            console.error('Error archiving campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isArchivingCampaign = false })
        }
    },

    unarchiveCampaign: async (campaignId) => {
        try {
            set((state) => { state.isArchivingCampaign = true })
            const response = await unarchiveCampaign_api({ campaignId });
            set((state) => {
                state.campaign = response
            })
            return response;
        } catch (error) {
            console.error('Error unarchiving campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isArchivingCampaign = false })
        }
    },

    resetCampaignDetails: () => {
        const {
            isLoadingCampaign,
        } = get()

        if (isLoadingCampaign) {
            return;
        }

        set((state) => {
            state.campaign = null
            state.campaignId = null
            state.campaignAnalyticsData = null
            state.marketStatsData = null
            state.impactStatsData = null
            state.isLoadingCampaign = true
            state.isLoadingCampaignAnalytics = true
            state.isLoadingMarketStatsData = true
            state.isLoadingImpactData = true
        })
    },

    adminRetryCampaign: async (campaignId: number, socialPlatform?: 'facebook' | 'tiktok') => {
        try {
            set((state) => { state.isRetryingCampaign = true })
            const response = await adminRetryCampaign_api({ campaignId, socialPlatform });
            return response;
        } catch (error) {
            console.error('Error retrying campaign:', error);
        } finally {
            set((state) => { state.isRetryingCampaign = false })
        }
    },

    downloadCampaignReport: async (campaignId: number) => {
        const { currentBrand } = useBrand.getState() as BrandExistsState

        try {
            set((state) => { state.isDownloadingReport = true })
            const currentBrandId = currentBrand?.id
            const response = await downloadCampaignReport_api({ campaignId, brandId: currentBrandId });
            return response;
        } catch (error) {
            console.error('Error downloading campaign report:', error);
        } finally {
            set((state) => { state.isDownloadingReport = false })
        }
    },
    pauseResumeAsset: async (campaignId: number, creativeId: string, pause: boolean): Promise<CampaignDetails> => {
        try {
            set((state) => { state.isPausingResumingAsset = true });
            const response = await pauseResumeAsset_api({
                campaignId,
                creativeId,
                pause
            });
            set((state) => {
                state.campaign = response
            });
            return response;
        } catch (error) {
            console.error('Error pausing/resuming asset:', error);
            throw error;
        } finally {
            set((state) => { state.isPausingResumingAsset = false });
        }
    },

    /** 
     * Upload new assets to an existing campaign
     * @param campaignId Campaign ID to add assets to
     * @param assets Array of CreativeSelectorPost objects representing the new assets
     * @returns Promise containing the updated campaign data
     */
    uploadNewAssets: async (campaignId: number, assets: CreativeSelectorPost[]): Promise<CampaignDetails> => {
        try {
            set((state) => { state.isUploadingNewAssets = true });
            
            const response = await uploadNewAssets_api(campaignId, assets);
            
            set((state) => {
                state.campaign = response;
            });
            
            return response;
        } catch (error) {
            console.error('Error uploading new assets:', error);
            throw error;
        } finally {
            set((state) => { state.isUploadingNewAssets = false });
        }
    },

    /**
     * Process an agent campaign (trigger AI agent to process it)
     * @param campaignId Campaign ID to process
     * @param brandId Optional brand ID for validation
     * @returns Promise containing the processing result
     */
    processAgentCampaign: async (campaignId: number, brandId?: number): Promise<any> => {
        try {
            set((state) => { state.isProcessingAgentCampaign = true });
            
            const response = await processAgentCampaign_api({
                campaignId,
                brandId
            });
            
            return response;
        } catch (error) {
            console.error('Error processing agent campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isProcessingAgentCampaign = false });
        }
    },

    /**
     * Extend a finished campaign with new end date and budget
     * @param campaignId Campaign ID to extend
     * @param newEndDate New end date for the campaign
     * @param newBudget New budget amount for the campaign
     * @returns Promise containing the response data
     */
    extendCampaign: async (campaignId: number, newEndDate: string, newBudget: number): Promise<any> => {
        try {
            set((state) => { state.isExtendingCampaign = true });
            
            const response = await extendCampaign_api({
                campaignId,
                newEndDate,
                newBudget
            });
            
            // Refresh campaign data after extending
            await get().getCampaign(campaignId);
            await get().getCampaignAnalytics(campaignId);

            return response;
        } catch (error) {
            console.error('Error extending campaign:', error);
            throw error;
        } finally {
            set((state) => { state.isExtendingCampaign = false });
        }
    },
})))

export default useCampaignDetails;